import { useAuth0 } from '@auth0/auth0-react';
import { useParams } from 'react-router';
import useSWRMutation from 'swr/mutation';
import http from '@/Utils/HTTP/NewHttpUtils';
import { useSearchCriteria } from './useSearchCriteria';
import { LanguageProficiency, SearchPeriod, useSearchFilters } from './useSearchFilters';
import useAISearchProgress from './useAISearchProgress';
import { GeocodeRequest } from './DTO/GeocodeRequest';

export interface SearchCriterionRequest {
    id: string;
    criterion: string;
}

export interface UpdateSearchParametersRequest {
    jobTitleSearchPeriod: SearchPeriod;
    jobTitles: string[];
    companiesSearchPeriod: SearchPeriod;
    companies: string[];
    locationRadius: number;
    locations: GeocodeRequest[];
    keywords: string[];
    minYearsOfExperience: number;
    maxYearsOfExperience: number;
    searchCriteria: Array<SearchCriterionRequest>;
    includeEmptyExperience: boolean;
    languages: string[];
    languageProficiency: LanguageProficiency;
}

const useUpdateSearchParameters = () => {
    const { getAccessTokenSilently } = useAuth0();
    const { assessmentId } = useParams();
    const { mutate: mutateSearchFilter } = useSearchFilters(assessmentId!);
    const { mutate: mutateSearchCriteria } = useSearchCriteria(assessmentId!);
    const { mutate: mutateAiSearchProgress } = useAISearchProgress();

    return useSWRMutation(
        `recruitments/${assessmentId}/ai-search/search-parameters`,
        async (path: string, { arg }: { arg: UpdateSearchParametersRequest }) => {
            await http.put({
                path,
                accessToken: await getAccessTokenSilently(),
                body: arg,
            });
        },
        {
            onSuccess: async () => {
                // When filters are updated, then we need to refetch the following data:
                // 1. search filters
                // 2. search criteria
                // 3. search progress status
                // 4. identified profiles will be refetch automatically when search progress status is false
                await Promise.all([mutateAiSearchProgress(), mutateSearchFilter(), mutateSearchCriteria()]);
            },
        },
    );
};

export default useUpdateSearchParameters;
